/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface ConfigurationSettingProps {
  id: string;
  companyId: string | null;
  defaultUnitCommittedDeclineReasonId: string | null;
  defaultUnitOutOfServiceDeclineReasonId: string | null;
  requestReferredInternallyCancellationReasonId: string | null;
  requestReferredExternallyCancellationReasonId: string | null;
  crewGoingByGroundOutOfServiceReasonId: string | null;
  assetReassignedDeclineReasonId: string | null;
  externallyCancelledCancellationReasonId: string | null;
  pilotReportGracePeriodInMinutes: number | null;
  sameDestinationWarningThresholdInMinutes: number | null;
  bugRequestEmail: string | null;
  featureRequestEmail: string | null;
  proximityAlertDistanceInNauticalMiles: number | null;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class ConfigurationSettingRecord extends Record<ConfigurationSettingProps>({
  id: null as any,
  companyId: null as any,
  defaultUnitCommittedDeclineReasonId: null as any,
  defaultUnitOutOfServiceDeclineReasonId: null as any,
  requestReferredInternallyCancellationReasonId: null as any,
  requestReferredExternallyCancellationReasonId: null as any,
  crewGoingByGroundOutOfServiceReasonId: null as any,
  assetReassignedDeclineReasonId: null as any,
  externallyCancelledCancellationReasonId: null as any,
  pilotReportGracePeriodInMinutes: null as any,
  sameDestinationWarningThresholdInMinutes: null as any,
  bugRequestEmail: null as any,
  featureRequestEmail: null as any,
  proximityAlertDistanceInNauticalMiles: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    companyId: string | null;
    defaultUnitCommittedDeclineReasonId: string | null;
    defaultUnitOutOfServiceDeclineReasonId: string | null;
    requestReferredInternallyCancellationReasonId: string | null;
    requestReferredExternallyCancellationReasonId: string | null;
    crewGoingByGroundOutOfServiceReasonId: string | null;
    assetReassignedDeclineReasonId: string | null;
    externallyCancelledCancellationReasonId: string | null;
    pilotReportGracePeriodInMinutes: number | null;
    sameDestinationWarningThresholdInMinutes: number | null;
    bugRequestEmail: string | null;
    featureRequestEmail: string | null;
    proximityAlertDistanceInNauticalMiles: number | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): ConfigurationSettingRecord {
    if (json == null) {
      throw new Error('ConfigurationSettingRecord.fromJS: parameter cannot be null');
    }
    return new ConfigurationSettingRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'ConfigurationSettingJson.id',
      ),
      companyId: json.companyId,
      defaultUnitCommittedDeclineReasonId: json.defaultUnitCommittedDeclineReasonId,
      defaultUnitOutOfServiceDeclineReasonId: json.defaultUnitOutOfServiceDeclineReasonId,
      requestReferredInternallyCancellationReasonId:
        json.requestReferredInternallyCancellationReasonId,
      requestReferredExternallyCancellationReasonId:
        json.requestReferredExternallyCancellationReasonId,
      crewGoingByGroundOutOfServiceReasonId: json.crewGoingByGroundOutOfServiceReasonId,
      assetReassignedDeclineReasonId: json.assetReassignedDeclineReasonId,
      externallyCancelledCancellationReasonId: json.externallyCancelledCancellationReasonId,
      pilotReportGracePeriodInMinutes: json.pilotReportGracePeriodInMinutes,
      sameDestinationWarningThresholdInMinutes: json.sameDestinationWarningThresholdInMinutes,
      bugRequestEmail: json.bugRequestEmail,
      featureRequestEmail: json.featureRequestEmail,
      proximityAlertDistanceInNauticalMiles: json.proximityAlertDistanceInNauticalMiles,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    companyId: string | null;
    defaultUnitCommittedDeclineReasonId: string | null;
    defaultUnitOutOfServiceDeclineReasonId: string | null;
    requestReferredInternallyCancellationReasonId: string | null;
    requestReferredExternallyCancellationReasonId: string | null;
    crewGoingByGroundOutOfServiceReasonId: string | null;
    assetReassignedDeclineReasonId: string | null;
    externallyCancelledCancellationReasonId: string | null;
    pilotReportGracePeriodInMinutes: number | null;
    sameDestinationWarningThresholdInMinutes: number | null;
    bugRequestEmail: string | null;
    featureRequestEmail: string | null;
    proximityAlertDistanceInNauticalMiles: number | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [ConfigurationSettingRecord, Error[]] {
    if (json == null) {
      throw new Error('ConfigurationSettingRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new ConfigurationSettingRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'ConfigurationSettingJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () => json.companyId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      defaultUnitCommittedDeclineReasonId: tryOrDefault(
        () => json.defaultUnitCommittedDeclineReasonId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      defaultUnitOutOfServiceDeclineReasonId: tryOrDefault(
        () => json.defaultUnitOutOfServiceDeclineReasonId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      requestReferredInternallyCancellationReasonId: tryOrDefault(
        () => json.requestReferredInternallyCancellationReasonId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      requestReferredExternallyCancellationReasonId: tryOrDefault(
        () => json.requestReferredExternallyCancellationReasonId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      crewGoingByGroundOutOfServiceReasonId: tryOrDefault(
        () => json.crewGoingByGroundOutOfServiceReasonId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      assetReassignedDeclineReasonId: tryOrDefault(
        () => json.assetReassignedDeclineReasonId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      externallyCancelledCancellationReasonId: tryOrDefault(
        () => json.externallyCancelledCancellationReasonId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      pilotReportGracePeriodInMinutes: tryOrDefault(
        () => json.pilotReportGracePeriodInMinutes,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      sameDestinationWarningThresholdInMinutes: tryOrDefault(
        () => json.sameDestinationWarningThresholdInMinutes,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      bugRequestEmail: tryOrDefault(
        () => json.bugRequestEmail,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      featureRequestEmail: tryOrDefault(
        () => json.featureRequestEmail,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      proximityAlertDistanceInNauticalMiles: tryOrDefault(
        () => json.proximityAlertDistanceInNauticalMiles,
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: ConfigurationSettingProps) {
    super(props);
    if (props == null) {
      throw new Error('ConfigurationSettingRecord constructor parameter cannot be null');
    }
  }
}

export abstract class ConfigurationSettingViewModelBase implements ConfigurationSettingProps {
  protected readonly _props: ConfigurationSettingProps;
  private _json?: ConfigurationSettingProps;

  get id(): string {
    return this._props.id;
  }
  get companyId(): string | null {
    return this._props.companyId;
  }
  get defaultUnitCommittedDeclineReasonId(): string | null {
    return this._props.defaultUnitCommittedDeclineReasonId;
  }
  get defaultUnitOutOfServiceDeclineReasonId(): string | null {
    return this._props.defaultUnitOutOfServiceDeclineReasonId;
  }
  get requestReferredInternallyCancellationReasonId(): string | null {
    return this._props.requestReferredInternallyCancellationReasonId;
  }
  get requestReferredExternallyCancellationReasonId(): string | null {
    return this._props.requestReferredExternallyCancellationReasonId;
  }
  get crewGoingByGroundOutOfServiceReasonId(): string | null {
    return this._props.crewGoingByGroundOutOfServiceReasonId;
  }
  get assetReassignedDeclineReasonId(): string | null {
    return this._props.assetReassignedDeclineReasonId;
  }
  get externallyCancelledCancellationReasonId(): string | null {
    return this._props.externallyCancelledCancellationReasonId;
  }
  get pilotReportGracePeriodInMinutes(): number | null {
    return this._props.pilotReportGracePeriodInMinutes;
  }
  get sameDestinationWarningThresholdInMinutes(): number | null {
    return this._props.sameDestinationWarningThresholdInMinutes;
  }
  get bugRequestEmail(): string | null {
    return this._props.bugRequestEmail;
  }
  get featureRequestEmail(): string | null {
    return this._props.featureRequestEmail;
  }
  get proximityAlertDistanceInNauticalMiles(): number | null {
    return this._props.proximityAlertDistanceInNauticalMiles;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: ConfigurationSettingProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        companyId: this._props.companyId,
        defaultUnitCommittedDeclineReasonId: this._props.defaultUnitCommittedDeclineReasonId,
        defaultUnitOutOfServiceDeclineReasonId: this._props.defaultUnitOutOfServiceDeclineReasonId,
        requestReferredInternallyCancellationReasonId:
          this._props.requestReferredInternallyCancellationReasonId,
        requestReferredExternallyCancellationReasonId:
          this._props.requestReferredExternallyCancellationReasonId,
        crewGoingByGroundOutOfServiceReasonId: this._props.crewGoingByGroundOutOfServiceReasonId,
        assetReassignedDeclineReasonId: this._props.assetReassignedDeclineReasonId,
        externallyCancelledCancellationReasonId:
          this._props.externallyCancelledCancellationReasonId,
        pilotReportGracePeriodInMinutes: this._props.pilotReportGracePeriodInMinutes,
        sameDestinationWarningThresholdInMinutes:
          this._props.sameDestinationWarningThresholdInMinutes,
        bugRequestEmail: this._props.bugRequestEmail,
        featureRequestEmail: this._props.featureRequestEmail,
        proximityAlertDistanceInNauticalMiles: this._props.proximityAlertDistanceInNauticalMiles,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllConfigurationSettingEntities = (state: AppState) =>
  state.entityStores.configurationSetting.entities;

export const selectConfigurationSettingById = (id: string) => (state: AppState) =>
  state.entityStores.configurationSetting.entities.get(id);

export const selectConfigurationSettingMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.configurationSetting.meta.get(id);

export const selectConfigurationSettingAndMetadataById = (id: string) =>
  createSelector(
    selectConfigurationSettingById(id),
    selectConfigurationSettingMetadataById(id),
    (configurationSetting, meta) => ({ configurationSetting, meta }),
  );

export function selectConfigurationSettingEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllConfigurationSettingEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, ConfigurationSettingRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectConfigurationSettingEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllConfigurationSettingEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}

export function selectConfigurationSettingEntitiesByDefaultUnitCommittedDeclineReasonId(
  defaultUnitCommittedDeclineReasonId: string,
) {
  return createSelector(selectAllConfigurationSettingEntities, (entities) =>
    entities
      .filter((e) => e.defaultUnitCommittedDeclineReasonId === defaultUnitCommittedDeclineReasonId)
      .valueSeq(),
  );
}

export function selectConfigurationSettingEntitiesByDefaultUnitOutOfServiceDeclineReasonId(
  defaultUnitOutOfServiceDeclineReasonId: string,
) {
  return createSelector(selectAllConfigurationSettingEntities, (entities) =>
    entities
      .filter(
        (e) => e.defaultUnitOutOfServiceDeclineReasonId === defaultUnitOutOfServiceDeclineReasonId,
      )
      .valueSeq(),
  );
}

export function selectConfigurationSettingEntitiesByRequestReferredInternallyCancellationReasonId(
  requestReferredInternallyCancellationReasonId: string,
) {
  return createSelector(selectAllConfigurationSettingEntities, (entities) =>
    entities
      .filter(
        (e) =>
          e.requestReferredInternallyCancellationReasonId ===
          requestReferredInternallyCancellationReasonId,
      )
      .valueSeq(),
  );
}

export function selectConfigurationSettingEntitiesByRequestReferredExternallyCancellationReasonId(
  requestReferredExternallyCancellationReasonId: string,
) {
  return createSelector(selectAllConfigurationSettingEntities, (entities) =>
    entities
      .filter(
        (e) =>
          e.requestReferredExternallyCancellationReasonId ===
          requestReferredExternallyCancellationReasonId,
      )
      .valueSeq(),
  );
}

export function selectConfigurationSettingEntitiesByCrewGoingByGroundOutOfServiceReasonId(
  crewGoingByGroundOutOfServiceReasonId: string,
) {
  return createSelector(selectAllConfigurationSettingEntities, (entities) =>
    entities
      .filter(
        (e) => e.crewGoingByGroundOutOfServiceReasonId === crewGoingByGroundOutOfServiceReasonId,
      )
      .valueSeq(),
  );
}

export function selectConfigurationSettingEntitiesByAssetReassignedDeclineReasonId(
  assetReassignedDeclineReasonId: string,
) {
  return createSelector(selectAllConfigurationSettingEntities, (entities) =>
    entities
      .filter((e) => e.assetReassignedDeclineReasonId === assetReassignedDeclineReasonId)
      .valueSeq(),
  );
}

export function selectConfigurationSettingEntitiesByExternallyCancelledCancellationReasonId(
  externallyCancelledCancellationReasonId: string,
) {
  return createSelector(selectAllConfigurationSettingEntities, (entities) =>
    entities
      .filter(
        (e) =>
          e.externallyCancelledCancellationReasonId === externallyCancelledCancellationReasonId,
      )
      .valueSeq(),
  );
}

export function selectConfigurationSettingEntitiesByPilotReportGracePeriodInMinutes(
  pilotReportGracePeriodInMinutes: number,
) {
  return createSelector(selectAllConfigurationSettingEntities, (entities) =>
    entities
      .filter((e) => e.pilotReportGracePeriodInMinutes === pilotReportGracePeriodInMinutes)
      .valueSeq(),
  );
}

export function selectConfigurationSettingEntitiesBySameDestinationWarningThresholdInMinutes(
  sameDestinationWarningThresholdInMinutes: number,
) {
  return createSelector(selectAllConfigurationSettingEntities, (entities) =>
    entities
      .filter(
        (e) =>
          e.sameDestinationWarningThresholdInMinutes === sameDestinationWarningThresholdInMinutes,
      )
      .valueSeq(),
  );
}

export function selectConfigurationSettingEntitiesByBugRequestEmail(bugRequestEmail: string) {
  return createSelector(selectAllConfigurationSettingEntities, (entities) =>
    entities.filter((e) => e.bugRequestEmail === bugRequestEmail).valueSeq(),
  );
}

export function selectConfigurationSettingEntitiesByFeatureRequestEmail(
  featureRequestEmail: string,
) {
  return createSelector(selectAllConfigurationSettingEntities, (entities) =>
    entities.filter((e) => e.featureRequestEmail === featureRequestEmail).valueSeq(),
  );
}

export function selectConfigurationSettingEntitiesByProximityAlertDistanceInNauticalMiles(
  proximityAlertDistanceInNauticalMiles: number,
) {
  return createSelector(selectAllConfigurationSettingEntities, (entities) =>
    entities
      .filter(
        (e) => e.proximityAlertDistanceInNauticalMiles === proximityAlertDistanceInNauticalMiles,
      )
      .valueSeq(),
  );
}
//#endregion Selectors
